<!--
  FRC-749 breakpoint: the desktop sidebar (and its 250px column) shows only at ≥1025px; at
  ≤1024px — phones and tablets, iPad landscape included — the bottom tab bar replaces it.
  Arbitrary variants rather than lg (992px) so exactly one nav is visible at every width. Both navs
  key off the same min-[1025px] query (hidden / shown), so fractional widths such as 1024.5px
  (zoom) cannot show both or neither.
-->
<div class="container-fluid max-[1024px]:tw-p-0">
  <div class="tw-flex tw-gap-5">
    <div
      class="tw-hidden tw-py-10 tw-pt-5 min-[1025px]:tw-block min-[1025px]:tw-max-w-[250px] min-[1025px]:tw-flex-[0_0_250px]"
    >
      <!-- Tabs -->
      <nav
        class="tw-sticky tw-top-20 tw-bg-white"
        aria-label="investor dashboard"
      >
        <ul class="-tw-mx-3 tw-my-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-px-0 tw-pt-4 tw-font-body">
          @for (tab of tabs; track tab.name) {
            @if (!tab.show || tab.show(role)) {
              @if (!tab?.submenus?.length) {
                <li>
                  <ng-container *ngTemplateOutlet="link; context: { item: tab }" />
                </li>
              } @else {
                <li>
                  <button
                    type="button"
                    class="tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-no-underline tw-transition-colors hover:tw-bg-divider-light hover:tw-no-underline focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-[-2px] focus-visible:tw-outline-primary"
                    [attr.aria-expanded]="isGroupExpanded(tab)"
                    (click)="toggleGroup(tab)"
                  >
                    <ng-container *ngTemplateOutlet="topLevelContent; context: { item: tab }" />
                  </button>
                  @if (isGroupExpanded(tab)) {
                    <ul
                      class="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-0 tw-p-0 tw-pl-5"
                    >
                      @for (subTab of tab.submenus; track subTab.url) {
                        @if (!subTab.show || subTab.show(role)) {
                          <li>
                            <ng-container
                              *ngTemplateOutlet="link; context: { item: subTab, isSub: true }"
                            />
                          </li>
                        }
                      }
                    </ul>
                  }
                </li>
              }
            }
          }
        </ul>
      </nav>
    </div>

    <div
      class="tw-w-full tw-min-w-[0] tw-pb-10 max-[1024px]:tw-pt-5 min-[1025px]:tw-flex-auto min-[1025px]:tw-py-10"
    >
      <router-outlet></router-outlet>
    </div>
  </div>

  <app-investor-bottom-nav
    [tabs]="tabs"
    [profileMenu]="profileMenu"
    [role]="role"
    [selectedMenu]="selectedMenu"
    [todoCount]="todoCount"
    [profileImage]="(profile && profile.photoAttachmentUrl) || defaultProfileImage"
    (logout)="logout()"
  />
</div>

<ng-template #link let-item="item" let-isSub="isSub">
  @if (item.isExternal) {
    <a
      class="tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-lg tw-px-3 tw-py-2.5 tw-no-underline tw-transition-colors hover:tw-bg-divider-light hover:tw-no-underline focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-[-2px] focus-visible:tw-outline-primary"
      [href]="item.url"
    >
      <ng-container
        *ngTemplateOutlet="isSub ? subContent : topLevelContent; context: { item: item }"
      />
    </a>
  } @else {
    <a
      class="tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-lg tw-px-3 tw-py-2.5 tw-no-underline tw-transition-colors hover:tw-bg-divider-light hover:tw-no-underline focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-[-2px] focus-visible:tw-outline-primary"
      [routerLink]="item.url"
      [class.tw-bg-divider-light]="selectedMenu === item.name"
      [attr.aria-current]="selectedMenu === item.name ? 'page' : null"
    >
      <ng-container
        *ngTemplateOutlet="isSub ? subContent : topLevelContent; context: { item: item }"
      />
    </a>
  }
</ng-template>

<ng-template #topLevelContent let-item="item">
  <span class="tw-flex tw-w-full tw-items-center tw-gap-3">
    @if (item.iconSrc) {
      <img
        class="tw-block tw-h-5 tw-w-5 tw-shrink-0"
        [src]="item.iconSrc"
        width="20"
        height="20"
        alt=""
      />
    }
    <span
      class="tw-min-w-0 tw-flex-1 tw-text-base tw-font-bold tw-leading-[1.3] tw-text-gray-800"
    >
      {{ item.name | translate }}
    </span>
    @let badge = badgeFor(item);
    @if (badge) {
      <span
        class="tw-flex tw-h-6 tw-min-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary tw-px-1.5 tw-text-base tw-font-bold tw-text-white"
        aria-hidden="true"
      >
        {{ badge }}
      </span>
      <span class="tw-sr-only">{{
        'dashboard.sidemenu.todoBadgeLabel' | translate: { count: badge }
      }}</span>
    }
    @if (item?.submenus?.length) {
      <img
        class="tw-block tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-transition-transform"
        [class.tw-rotate-180]="!isGroupExpanded(item)"
        src="assets/img/icons/investor-sidemenu/chevron.svg"
        width="18"
        height="18"
        alt=""
      />
    }
  </span>
</ng-template>

<ng-template #subContent let-item="item">
  <span class="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
    <span class="tw-text-[15px] tw-font-semibold tw-leading-[1.5] tw-text-gray-800">
      {{ item.name | translate }}
    </span>
    @if (item.description) {
      <span class="tw-text-sm tw-font-normal tw-leading-[1.6] tw-text-divider-dark">
        {{ item.description | translate }}
      </span>
    }
  </span>
</ng-template>
